<template>
  <v-container fluid class="pb-12">
    <v-card class="results-card">
      <div class="card-section-header px-6 py-4">
        <div class="d-flex align-center flex-wrap" style="gap:10px;">
          <v-text-field v-model="supplier_name" dense outlined hide-details placeholder="Search name, email or product"
            prepend-inner-icon="mdi-magnify" clearable style="width:300px;" class="header-filter-input"
            @input="onSearchInput()" @keyup.enter="search()" />
          <v-autocomplete v-model="supplier_types" dense outlined hide-details placeholder="All Supplier Types"
            :items="get_supplier_type" item-text="name" item-value="id" multiple small-chips deletable-chips
            clearable style="width:240px;" class="header-filter-input" :class="{ 'show-placeholder': !(supplier_types && supplier_types.length) }" @change="search()" />
          <v-autocomplete v-model="location_ids" dense outlined hide-details placeholder="All Locations"
            :items="get_locations" item-text="name" item-value="id" multiple small-chips deletable-chips
            clearable style="width:240px;" class="header-filter-input" :class="{ 'show-placeholder': !(location_ids && location_ids.length) }" @change="search()" />
          <v-switch v-model="show_archived" dense hide-details dark inset label="Show archived" class="mt-0 ml-2"
            @change="search()" />
          <v-spacer></v-spacer>
          <v-btn outlined depressed class="suppliers-header-btn header-outline-btn" @click="exportCsv()"
            :loading="exporting">
            <v-icon left>mdi-download</v-icon>
            Export
          </v-btn>
          <v-btn outlined depressed class="suppliers-header-btn header-outline-btn" @click="getSuppliers()"
            :loading="searching">
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
          <v-btn depressed class="suppliers-header-btn header-create-btn" @click="$router.push({ name: '/create-supplier' })">
            <v-icon left>mdi-plus</v-icon>
            Create
          </v-btn>
        </div>
        <div v-if="hasActiveFilters" class="header-clear-row">
          <v-btn x-small text class="header-clear-btn" @click="resetFilter()">
            <v-icon left x-small>mdi-close</v-icon>
            Clear filters
          </v-btn>
        </div>
      </div>

      <!-- Loading State -->
      <v-card-text v-if="searching" class="text-center py-16">
        <v-progress-circular :size="60" :width="5" color="primary" indeterminate class="mb-4" />
        <div class="grey--text text--darken-1">Loading suppliers...</div>
      </v-card-text>

      <template v-else>
        <v-data-table :headers="headers_all" :items="suppliers" :server-items-length="total_suppliers" :page.sync="page"
          :items-per-page="per_page" :footer-props="{ itemsPerPageOptions: [10, 15, 25, 50] }" :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc" @update:page="getSuppliers" @update:items-per-page="onPerPageChange"
          hide-default-header>
          <template v-slot:header>
            <thead class="supplier-table-head">
              <tr>
                <th style="width:110px;"></th>
                <th>Supplier</th>
                <th>Contact</th>
                <th>Type</th>
                <th style="width:22%;">Locations</th>
                <th>Services</th>
                <th style="width:80px;">Actions</th>
              </tr>
            </thead>
          </template>

          <template v-slot:item="{ item }">
            <tr class="supplier-row" @click="selectSupplier(item)">
              <td class="supplier-img-cell">
                <div class="supplier-img-wrap">
                  <img v-if="item.image" :src="api_url + 'media/suppliers/images/' + item.image" class="supplier-img" />
                  <img v-else-if="item.sup_logo" :src="api_url + 'media/suppliers/images/' + item.sup_logo"
                    class="supplier-img" />
                  <img v-else-if="item.products?.length > 0 && item.products[0].image"
                    :src="api_url + 'media/suppliers/images/' + item.products[0].image" class="supplier-img" />
                  <div v-else class="supplier-img-placeholder">
                    <v-icon size="36" color="#C8C8C8">mdi-image-off-outline</v-icon>
                  </div>
                </div>
              </td>
              <td>
                <a :href="$router.resolve({ name: '/supplier-setup', params: { supplier_id: item.id } }).href"
                  class="supplier-name" @click.prevent>{{ item.supplier_name }}</a>
              </td>
              <td>
                <div class="contact-email">{{ item.email }}</div>
                <div v-if="item.contact_name" class="contact-secondary">{{ item.contact_name }}</div>
                <div v-if="item.phone" class="contact-secondary">{{ item.phone }}</div>
              </td>
              <td>
                <div class="type-chips">
                  <v-chip v-for="(name, i) in supplierTypeNames(item)" :key="i" small label class="type-chip mr-1 mb-1">
                    {{ name }}
                  </v-chip>
                  <span v-if="supplierTypeNames(item).length === 0" class="grey--text text--lighten-1">—</span>
                </div>
              </td>
              <td>
                <div class="location-chips">
                  <v-chip v-for="(sl, index) in item.supplier_locations" :key="index" x-small label
                    class="location-chip mr-1 mb-1">
                    {{ sl.location.name }}
                  </v-chip>
                </div>
              </td>
              <td>
                <div class="services-count">
                  <v-icon small color="#669E53" class="mr-1">mdi-briefcase-outline</v-icon>
                  {{item.products.filter((x) => !x.archived).length}}
                  <span class="grey--text text--darken-1 ml-1">services</span>
                </div>
              </td>
              <td class="actions-cell" @click.stop>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" class="actions-btn">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="showCopyToCompanyDialog(item)">
                      <v-list-item-icon>
                        <v-icon small>mdi-domain</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Copy Supplier To Company</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="showDuplicateDialog(item)">
                      <v-list-item-icon>
                        <v-icon small>mdi-content-copy</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Duplicate Supplier</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="show_archived" @click="restoreSupplier(item)">
                      <v-list-item-icon>
                        <v-icon small>mdi-restore</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Restore Supplier</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-else @click="showDeleteDialog(item)" class="red--text">
                      <v-list-item-icon>
                        <v-icon small color="red">mdi-delete</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Delete Supplier</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-12">
              <v-icon size="64" color="#E0E0E0" class="mb-4">mdi-store-search-outline</v-icon>
              <div class="grey--text text--darken-1">No suppliers match your search criteria</div>
              <v-btn text color="primary" class="mt-3" @click="resetFilter()">Clear filters</v-btn>
            </div>
          </template>
        </v-data-table>
      </template>
    </v-card>

    <!-- Copy To Company Dialog -->
    <v-dialog v-model="copyToCompanyDialog" max-width="480px" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="primary">mdi-domain</v-icon>
          Copy Supplier To Company
        </v-card-title>
        <v-card-text>
          Copy <strong>{{ selectedSupplier?.supplier_name }}</strong> with all its data (services, options, rates,
          locations, contacts, documents) to:
          <v-autocomplete v-model="copyCompanyIds" :items="copyTargetCompanies" item-text="company_name" item-value="id"
            label="Companies" multiple chips small-chips deletable-chips outlined dense hide-details class="mt-4" />
          <div class="caption grey--text text--darken-1 mt-3">
            Each company gets its own independent copy. Locations and service types are matched by name in the target
            company and created there if missing.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="copyToCompanyDialog = false; selectedSupplier = null" :disabled="copyingToCompany">
            Cancel
          </v-btn>
          <v-btn color="primary" @click="copySupplierToCompanies" :loading="copyingToCompany"
            :disabled="!copyCompanyIds.length">
            Copy
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Duplicate Confirmation Dialog -->
    <v-dialog v-model="duplicateDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="primary">mdi-content-copy</v-icon>
          Duplicate Supplier
        </v-card-title>
        <v-card-text>
          Are you sure you want to duplicate <strong>{{ selectedSupplier?.supplier_name }}</strong>?
          <br><br>
          This will create an exact copy of the supplier with all their products and location assignments.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="duplicateDialog = false; selectedSupplier = null" :disabled="duplicating">
            Cancel
          </v-btn>
          <v-btn color="primary" @click="duplicateSupplier" :loading="duplicating">
            Duplicate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="text-h6 red--text">
          <v-icon left color="red">mdi-delete</v-icon>
          Delete Supplier
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete <strong>{{ selectedSupplier?.supplier_name }}</strong>?
          <br><br>
          <v-alert type="info" dense outlined class="mt-3">
            The supplier is archived: it stays on existing quotes and events but no longer shows in the suppliers
            list or the quote builder. You can restore it from "Show archived".
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false; selectedSupplier = null" :disabled="deleting">
            Cancel
          </v-btn>
          <v-btn color="red" @click="deleteSupplier" :loading="deleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import pageBadgesMixin from "@/common/PageNameStore/pageBadgesMixin"
import { lookups, loadLookups } from "@/common/companyLookups"
export default {
  mixins: [pageBadgesMixin],
  components: {},
  props: [],
  data: () => ({
    suppliers: [],
    supplier_name: null,
    supplier_types: [],
    location_ids: [],
    show_archived: false,
    page: 1,
    per_page: 15,
    total_suppliers: 0,
    sortBy: 'supplier_name',
    sortDesc: false,
    searching: true,
    exporting: false,
    headers_all: [
      { text: '', sortable: false },
      { text: 'Supplier', value: 'supplier_name' },
      { text: 'Contact', sortable: false },
      { text: 'Type', value: 'service_type_id' },
      { text: 'Locations', sortable: false },
      { text: 'Services', sortable: false },
      { text: 'Actions', sortable: false, width: '80px' },
    ],
    api_url: null,
    // Dialog states
    duplicateDialog: false,
    deleteDialog: false,
    selectedSupplier: null,
    duplicating: false,
    deleting: false,
    copyToCompanyDialog: false,
    copyCompanyIds: [],
    copyingToCompany: false,
  }),
  async mounted() {
    this.api_url = process.env.VUE_APP_API_URL
    await Promise.all([
      loadLookups('locations'),
      this.$store.dispatch('supplier/fetch_supplier_type'),
    ])
    this.getSuppliers()
  },
  created() {
    this.setPageName('Suppliers')
  },
  computed: {
    get_locations() {
      return lookups.locations
    },
    ...mapGetters({
      get_user: 'auth/get_user',
      get_selected_company: 'auth/get_selected_company',
      get_supplier_type: 'supplier/get_supplier_type',
      get_companies: 'auth/get_companies',
    }),
    copyTargetCompanies() {
      return (this.get_companies || []).filter((c) => c.id !== this.get_selected_company.id)
    },
    hasActiveFilters() {
      return !!(this.supplier_name || (this.supplier_types && this.supplier_types.length) || (this.location_ids && this.location_ids.length))
    },
    pageBadges() {
      return [this.total_suppliers.toLocaleString()]
    },
  },
  methods: {
    ...mapActions({
      setPageName: 'pageNameStore/setPageNameAction',
    }),
    search() {
      this.page = 1
      this.getSuppliers()
    },
    // Called by the AI Assistant after it changes data: same filters and search
    aiRefresh() {
      return this.getSuppliers()
    },
    async getSuppliers() {
      const params = {
        supplier_name: this.supplier_name || undefined,
        page: this.page,
        per_page: this.per_page,
        archived: this.show_archived ? 1 : undefined,
      }
      if (this.supplier_types && this.supplier_types.length) {
        params.service_type_id = this.supplier_types.join(',')
      }
      if (this.location_ids && this.location_ids.length) {
        params.location_ids = this.location_ids.join(',')
      }
      this.searching = true
      const { data } = await this.$axios.get(`admin/suppliers/get_suppliers/${this.get_selected_company.id}`, params)
      if (data.response) {
        this.suppliers = data.data
        this.total_suppliers = data.total
      }
      this.searching = false
    },
    onPerPageChange(val) {
      this.per_page = val
      this.page = 1
      this.getSuppliers()
    },
    onSearchInput() {
      clearTimeout(this._search_timer)
      this._search_timer = setTimeout(() => this.search(), 350)
    },
    resetFilter() {
      this.supplier_types = []
      this.supplier_name = null
      this.location_ids = []
      this.page = 1
      this.getSuppliers()
    },
    selectSupplier(item) {
      this.$router.push({ name: '/supplier-setup', params: { supplier_id: item.id } })
    },
    supplierTypeNames(item) {
      const ids = (item.service_type_ids && item.service_type_ids.length) ? item.service_type_ids : (item.service_type_id ? [item.service_type_id] : [])
      return ids.map((id) => this.get_supplier_type.find((t) => t.id === id)?.name).filter(Boolean)
    },
    async exportCsv() {
      const params = {
        supplier_name: this.supplier_name || undefined,
        archived: this.show_archived ? 1 : undefined,
      }
      if (this.supplier_types && this.supplier_types.length) {
        params.service_type_id = this.supplier_types.join(',')
      }
      if (this.location_ids && this.location_ids.length) {
        params.location_ids = this.location_ids.join(',')
      }
      this.exporting = true
      try {
        const { data } = await this.$axios.get(
          `admin/suppliers/export_suppliers/${this.get_selected_company.id}`,
          params
        )
        if (!data.response) return
        const rows = data.data
        const headers = ['Supplier Name', 'Email', 'Contact Name', 'Phone', 'Type', 'Locations', 'Active Services']
        const csvLines = [headers.join(',')]
        rows.forEach((item) => {
          const locations = (item.supplier_locations || [])
            .map((sl) => sl.location?.name || '')
            .join('; ')
          const activeServices = (item.products || []).filter((x) => !x.archived).length
          const row = [
            this.csvEscape(item.supplier_name),
            this.csvEscape(item.email),
            this.csvEscape(item.contact_name),
            this.csvEscape(item.phone),
            this.csvEscape(this.supplierTypeNames(item).join('; ')),
            this.csvEscape(locations),
            activeServices,
          ]
          csvLines.push(row.join(','))
        })
        const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'suppliers.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } finally {
        this.exporting = false
      }
    },
    csvEscape(value) {
      if (value == null) return ''
      const str = String(value)
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"'
      }
      return str
    },
    showDuplicateDialog(supplier) {
      this.selectedSupplier = supplier
      this.duplicateDialog = true
    },
    showCopyToCompanyDialog(supplier) {
      this.selectedSupplier = supplier
      this.copyCompanyIds = []
      this.copyToCompanyDialog = true
    },
    async copySupplierToCompanies() {
      if (!this.selectedSupplier || !this.copyCompanyIds.length) return
      this.copyingToCompany = true
      try {
        const { data } = await this.$axios.post(
          `admin/suppliers/copy_supplier_to_companies/${this.selectedSupplier.id}`,
          { company_ids: this.copyCompanyIds }
        )
        if (data.response) {
          this.$toast.success(data.message || 'Supplier copied')
          this.copyToCompanyDialog = false
          this.selectedSupplier = null
        } else {
          this.$toast.error(data.message || 'Failed to copy supplier')
        }
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || 'An error occurred while copying the supplier')
        console.error('Copy supplier to company error:', error)
      } finally {
        this.copyingToCompany = false
      }
    },
    showDeleteDialog(supplier) {
      this.selectedSupplier = supplier
      this.deleteDialog = true
    },
    async duplicateSupplier() {
      if (!this.selectedSupplier) return
      
      this.duplicating = true
      try {
        const { data } = await this.$axios.post(
          `admin/suppliers/duplicate_supplier/${this.selectedSupplier.id}`
        )
        if (data.response) {
          this.$toast.success('Supplier duplicated successfully')
          this.getSuppliers() // Refresh the list
        } else {
          this.$toast.error(data.message || 'Failed to duplicate supplier')
        }
      } catch (error) {
        this.$toast.error('An error occurred while duplicating the supplier')
        console.error('Duplicate supplier error:', error)
      } finally {
        this.duplicating = false
        this.duplicateDialog = false
        this.selectedSupplier = null
      }
    },
    async restoreSupplier(supplier) {
      try {
        const { data } = await this.$axios.post(`admin/suppliers/restore_supplier/${supplier.id}`)
        if (data.response) {
          this.$toast.success('Supplier restored')
          this.getSuppliers()
        } else {
          this.$toast.error(data.message || 'Failed to restore supplier')
        }
      } catch (error) {
        this.$toast.error('An error occurred while restoring the supplier')
        console.error('Restore supplier error:', error)
      }
    },
    async deleteSupplier() {
      if (!this.selectedSupplier) return
      
      this.deleting = true
      try {
        const { data } = await this.$axios.delete(
          `admin/suppliers/delete_supplier/${this.selectedSupplier.id}`
        )
        if (data.response) {
          this.$toast.success('Supplier archived')
          this.getSuppliers() // Refresh the list
        } else {
          this.$toast.error(data.message || 'Failed to delete supplier')
        }
      } catch (error) {
        this.$toast.error('An error occurred while deleting the supplier')
        console.error('Delete supplier error:', error)
      } finally {
        this.deleting = false
        this.deleteDialog = false
        this.selectedSupplier = null
      }
    },
  },
  watch: {},
}
</script>

<style lang="scss" scoped>
/* Header */
.card-section-header {
  background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.header-filter-input ::v-deep .v-input__control {
  background: white;
  border-radius: 4px;
}

// Vuetify collapses a chips autocomplete's input until focused, which hides the placeholder;
// keep it visible while nothing is selected.
.show-placeholder ::v-deep input {
  max-height: none !important;
  padding: 8px 0 !important;
}

.header-clear-row {
  margin: 6px 0 -8px -8px;
}

.header-clear-btn {
  font-family: Manrope;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: none;
  color: white !important;
}

.header-outline-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  color: white !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.header-create-btn {
  background: white !important;
  color: #3988AA !important;
}

.suppliers-header-btn {
  font-family: Manrope;
  font-weight: 600 !important;
  font-size: 13px !important;
  text-transform: none;
  height: 36px !important;
  padding: 0 14px !important;
}

/* Results Card */
.results-card {
  border-radius: 10px !important;
  border: 1px solid #E9E9E9 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  background-color: #fff;
  overflow: hidden;
}

/* Table Head */
.supplier-table-head th {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #9E9E9E;
  background-color: #FAFAFA;
  padding: 10px 16px;
  border-bottom: 1px solid #F0F0F0;
  white-space: nowrap;
}

/* Rows */
.supplier-row {
  cursor: pointer;
  transition: background-color 0.15s ease;

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #F5F5F5 !important;
    vertical-align: middle;
  }

  &:hover {
    background-color: #F7F9FF !important;
  }

  &:last-child td {
    border-bottom: none !important;
  }
}

/* Image cell */
.supplier-img-cell {
  padding: 10px 12px !important;
}

.supplier-img-wrap {
  width: 90px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.supplier-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.supplier-img-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* Name */
.supplier-name {
  font-size: 14px;
  font-weight: 600;
  color: #3988AA;
  display: inline-block;
  text-decoration: underline;
}

/* Contact */
.contact-email {
  font-size: 13px;
  font-weight: 600;
  color: #444;
}

.contact-secondary {
  font-size: 12px;
  color: #9E9E9E;
  margin-top: 2px;
}

/* Type chip */
.type-chips {
  display: flex;
  flex-wrap: wrap;
}

.type-chip {
  background-color: #F0F4FF !important;
  color: #3D5AFE !important;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.2px;
  border-radius: 6px !important;
}

/* Location chips */
.location-chips {
  display: flex;
  flex-wrap: wrap;
  max-height: 80px;
  overflow-y: auto;
  align-content: flex-start;
}

.location-chip {
  background-color: #E6F3E2 !important;
  color: #4A8C3A !important;
  font-size: 11px !important;
  font-weight: 600;
  border-radius: 5px !important;
}

/* Services */
.services-count {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #2C2C2C;
  white-space: nowrap;
}

/* Data table overrides */
:deep(.v-data-table__wrapper) {
  overflow-x: auto;
}

:deep(.v-data-footer) {
  background-color: #FAFAFA;
  border-top: 1px solid #F0F0F0;
  font-size: 13px;
}

:deep(.v-data-footer__select .v-text-field) {
  background-color: #fff;
}

:deep(tbody tr:hover) {
  background-color: transparent !important;
}

:deep(.v-chip .v-chip__content) {
  font-size: 11px;
}

/* Actions column */
.actions-cell {
  padding: 8px 12px !important;
}

.actions-btn {
  transition: all 0.15s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}
</style>